import React, { useState, useEffect } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { getEstimatedIncentivePayOutDetails } from "../../../store/actions";
import { formatCurrency } from "../../../utils";

const POINT_PLAN_CONFIG = [
  {
    key: "totaldollars",
    title: "Doll/Hr",
    headers: ["Doll/Hr", "Points Earned"],
  },
  {
    key: "productivity",
    title: "Productivity",
    headers: ["Productivity", "Points Earned"],
  },
  {
    key: "processquality",
    title: "Process Quality",
    headers: ["Process Quality", "Points Earned"],
  },
  {
    key: "letterquality",
    title: "Letter Quality",
    headers: ["Letter Quality", "Points Earned"],
  },
  {
    key: "phonequality",
    title: "Phone Quality",
    headers: ["Phone Quality", "Points Earned"],
  },
  {
    key: "monthlypayout",
    title: "MONTHLY Payout",
    isMonthly: true,
    headers: ["Points Earned", "PAY OUT"],
  },
];

const AgentScheduleModal = ({
  clickedRow,
  showModal,
  toggleModal,
  estimatedIncentivePayOut,
}) => {
  const dispatch = useDispatch();
  const { activeOrganizationInfo, agentInsentiveInfo, isFetching } =
    useSelector(
      ({
        get_active_organization_detail: { data: activeOrganizationData },
        get_estimated_incentive_payout_details: {
          data: agentInsentiveData,
          isFetching,
        },
      }) => {
        return {
          activeOrganizationInfo: activeOrganizationData || {},
          agentInsentiveInfo: agentInsentiveData,
          isFetching: isFetching,
        };
      },
      shallowEqual,
    );
  const closeBtn = (
    <div
      className="links_right"
      style={{ position: "absolute", top: "15px", right: "15px" }}
    >
      <button
        className="modal_close bg-transparent"
        onClick={toggleModal}
        type="button"
      >
        <img src="/images/cross.svg" />
      </button>
    </div>
  );
  const groupByMonthYear = (data) => {
    return data.reduce((acc, item) => {
      if (!acc[item.monthYear]) {
        acc[item.monthYear] = [];
      }
      acc[item.monthYear].push(item);
      return acc;
    }, {});
  };
  useEffect(() => {
    dispatch(
      getEstimatedIncentivePayOutDetails({
        companyguid: activeOrganizationInfo?.Companyguid,
        organization: activeOrganizationInfo?.Organization,
        primaryprogram: activeOrganizationInfo?.Program,
        employeeid: activeOrganizationInfo?.Employeeid,
        recordtype: "PopUpDetails",
      }),
    );
  }, [dispatch]);

  return (
    <Modal
      isOpen={showModal}
      toggle={toggleModal}
      backdrop={true}
      keyboard={true}
      size="xl"
      className="fullscreen-modal"
      // centered={true}
      external={closeBtn}
      id="estimated-incentive-payOut-details"
    >
      <ModalBody>
        <div className="agent-incentive-layout">
          {/* ===== LEFT TOP ===== */}
          <div className="left-top">
            <div className="section-title">
              My Monthly Estimated PAYOUT <span>|</span> MTD PAYOUT
            </div>

            {/* Monthly table + MTD tile */}
            <div className="monthly-mtd">
              {/* Monthly table */}
              <div className="monthly-table">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>METRIC</th>
                      <th>ACTUAL</th>
                      <th>GOAL</th>
                      <th>POINTS EARNED</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentInsentiveInfo?.monthlyEstimatedValue
                      ?.filter((x) => x.metricname?.toLowerCase() !== "total")
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.metricname}</td>
                          <td>{item.actualvalue}</td>
                          <td>{item.goal}</td>
                          <td>{item.pointsEarned}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>

              {/* MTD tile */}
              <div className="mtd-tile">
                <h6>MTD Estimated Incentive PAYOUT</h6>
                <div className="mtd-value">
                  {estimatedIncentivePayOut?.length > 0
                    ? formatCurrency(estimatedIncentivePayOut[0].incentiveValue)
                    : "$0.00"}
                </div>
              </div>
            </div>
          </div>

          {/* ===== LEFT BOTTOM ===== */}
          <div className="left-bottom ppm-section">
            <div className="section-title">Point Plan Matrix</div>

            <div className="matrix-grid">
              {POINT_PLAN_CONFIG.map((cfg) => (
                <table key={cfg.key} className="table table-bordered">
                  <thead>
                    {/* ===== Monthly Payout special header ===== */}
                    {cfg.isMonthly ? (
                      <>
                        <tr>
                          <th colSpan={2}>{cfg.title}</th>
                        </tr>
                        <tr>
                          {cfg.headers.map((h) => (
                            <th key={h}>{h}</th>
                          ))}
                        </tr>
                      </>
                    ) : (
                      <tr>
                        {cfg.headers.map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    )}
                  </thead>

                  <tbody>
                    {agentInsentiveInfo?.pointPlanMatrix
                      ?.filter(
                        (x) =>
                          x.metricname?.toLowerCase().replace(" ", "") ===
                          cfg.key,
                      )
                      .map((item, idx) => (
                        <tr key={idx}>
                          <td>
                            <b>{item.pointsRange}</b>
                          </td>
                          <td>{item.pointsEarned}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ))}
            </div>
          </div>

          {/* ===== RIGHT (YTD) ===== */}
          <div className="right-ytd">
            <div className="section-title">Year-to-date</div>

            <div className="ytd-scroll">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>PAYOUT</th>
                  </tr>
                </thead>
                <tbody>
                  {agentInsentiveInfo?.yeartoDate?.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.monthYear}</td>
                      <td>{formatCurrency(item.incentiveValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
export default AgentScheduleModal;
